/**
 * @fileoverview NotificationModal Component
 * 
 * A custom notification modal that replaces browser's alert()
 * with a styled modal matching the application's blue gradient design system.
 * 
 * Features:
 * - Blue gradient background with white border
 * - Icon-based visual feedback (success, error, warning, info)
 * - Auto-dismiss option with countdown
 * - Keyboard accessibility (ESC or Enter to close)
 * - Mobile responsive
 * - Prevents body scroll when open
 * 
 * @exports NotificationModal
 */

import { useEffect, useState } from "react";
import "../styles/components/NotificationModal.css";

/**
 * NotificationModal Component
 * 
 * Displays a notification message with customizable type and auto-dismiss functionality.
 * Used to replace browser alerts with a more user-friendly interface.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback invoked when modal is closed
 * @param {string} props.type - Notification type: "success", "error", "warning", "info"
 * @param {string} props.title - Modal title text (optional, defaults based on type)
 * @param {string} props.message - Main notification message
 * @param {number} [props.autoDismiss=0] - Auto-dismiss after X seconds (0 = no auto-dismiss)
 * @param {string} [props.buttonText="OK"] - Text for close button
 * @param {boolean} [props.isMobile=false] - Mobile view flag
 * 
 * @returns {JSX.Element|null} The notification modal or null if not open
 * 
 * @example
 * <NotificationModal
 *   isOpen={showNotification}
 *   onClose={() => setShowNotification(false)}
 *   type="success"
 *   message="Caregiver deleted successfully!"
 *   autoDismiss={3}
 * />
 */
export default function NotificationModal({
  isOpen,
  onClose,
  type = "info", // success, error, warning, info
  title,
  message,
  autoDismiss = 0, // Auto-dismiss after X seconds (0 = no auto-dismiss)
  buttonText = "OK",
  isMobile = false,
}) {
  const [countdown, setCountdown] = useState(autoDismiss);

  // Handle ESC and Enter keys to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e) => {
      if (e.key === "Escape" || e.key === "Enter") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Auto-dismiss countdown
  useEffect(() => {
    if (!isOpen || autoDismiss === 0) return;

    setCountdown(autoDismiss);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, autoDismiss, onClose]);

  if (!isOpen) return null;

  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case "success":
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
        );
      case "error":
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/>
            <path d="M15 9l-6 6M9 9l6 6"/>
          </svg>
        );
      case "warning":
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        );
      case "info":
      default:
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        );
    }
  };

  // Get default title based on type
  const getTitle = () => {
    if (title) return title;
    
    switch (type) {
      case "success":
        return "Success";
      case "error":
        return "Error";
      case "warning":
        return "Warning";
      case "info":
      default:
        return "Information";
    }
  };

  return (
    <div className={`notification-modal-overlay ${isMobile ? "mobile" : ""}`}>
      <div 
        className="notification-modal-backdrop"
        onClick={onClose}
      />
      
      <div className={`notification-modal ${type} ${isMobile ? "mobile" : ""}`}>
        {/* Icon */}
        <div className={`notification-modal__icon notification-modal__icon--${type}`}>
          {getIcon()}
        </div>

        {/* Title */}
        <h2 className="notification-modal__title">
          {getTitle()}
        </h2>

        {/* Message */}
        <p className="notification-modal__message">
          {message}
        </p>

        {/* Button with optional countdown */}
        <button 
          onClick={onClose} 
          className={`notification-modal__button notification-modal__button--${type}`}
          type="button"
        >
          {buttonText}
          {countdown > 0 && ` (${countdown}s)`}
        </button>
      </div>
    </div>
  );
}
